import { defineStore } from 'pinia';
import { clearAllCookies, hasCookie, readCookie, readRawCookie, writeCookie } from '@/modules/cookies';
import { decodeBase64, parseJwt } from '@/modules/jwt';
import { logger } from '@/logging/logger';
import { SW_GENERATION_CACHE } from '@/utils/serviceWorkerUpdate';

const LOGIN_COOKIE_MONTHS = 12;

export interface EngagementLogin {
  name: string;
  token: string;
  [key: string]: any;
}

export interface LoginPayload {
  token: string;
  loginLang?: string;
}

export const getLoginDetails = (payload: LoginPayload | string) => {
  const token = typeof payload === 'object' ? payload.token : payload
  const loginLang = typeof payload === 'object' ? payload.loginLang : undefined

  return {
    token,
    redirect: loginLang
      ? `/#/setup?loginLang=${encodeURIComponent(loginLang)}`
      : '/',
  }
};

// actions, not getters: cookie reads have nothing reactive to cache on
export const useAuthStore = defineStore('auth', {
  actions: {
    authenticated(): boolean {
      return hasCookie('session') && hasCookie('token');
    },

    logins(): EngagementLogin[] {
      const token = readRawCookie('token')
      if (!token) return []

      try {
        const data = parseJwt(token)
        return (data?.scopes?.engagements || []).map((x: string) => decodeBase64(x))
      }
      catch (e) {
        return []
      }
    },

    key(): string | null {
      return readCookie('session');
    },

    async login(payload: LoginPayload | string): Promise<void> {
      logger.info('auth', 'session.login', undefined, 'Session login initiated').then();
      const { token, redirect } = getLoginDetails(payload)
      let now = new Date()
      now.setMonth(now.getMonth() + LOGIN_COOKIE_MONTHS)
      writeCookie('session', token, now)
      logger.info('auth', 'session.cookie.set', undefined, 'Session cookie set').then();
      document.location.href = redirect
    },

    async logout(): Promise<void> {
      logger.info('auth', 'session.logout', undefined, 'Session logout initiated').then();

      clearAllCookies()

      try {
        localStorage.clear()
      } catch (error: any) {
        logger.error('storage', 'storage.key.failure', { key: 'localStorage', operation: 'clear', message: error?.message }, 'Failed clearing localStorage during logout').then();
      }

      try {
        sessionStorage.clear()
      } catch (error: any) {
        logger.error('storage', 'storage.key.failure', { key: 'sessionStorage', operation: 'clear', message: error?.message }, 'Failed clearing sessionStorage during logout').then();
      }

      try {
        const cacheNames = await caches.keys()
        await Promise.all(
          cacheNames
            .filter(name => name !== SW_GENERATION_CACHE)
            .map(name => caches.delete(name)),
        )
      } catch (error: any) {
        logger.error('storage', 'storage.key.failure', { key: 'cacheStorage', operation: 'clear', message: error?.message }, 'Failed clearing cache storage during logout').then();
      }

      document.location.href = '/'
    },
  },
})
